import { createServerFn } from "@tanstack/react-start";
import { and, eq, isNull, sql } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import { db } from "~/lib/db";
import type { cslTypeEnum, sourceNameEnum } from "~/lib/db/schema";
import {
  canonicalReferences,
  citationSourceLinks,
  user as dbUser,
  importBatches,
  projectCitations,
  projectScreeningSettings,
  screeningDecisions,
  sourceRecords,
} from "~/lib/db/schema";
import { detectDuplicatesForProject } from "~/lib/duplicates/detection";
import { fetchPubmedCitations } from "~/lib/pubmed/client";
import {
  isNbibFormat,
  parseNbibContent,
  type ParsedNbibEntry,
} from "~/lib/pubmed/nbib-parser";
import type { ParsedPubmedEntry } from "~/lib/pubmed/parser";
import { parsePubmedXml } from "~/lib/pubmed/parser";
import { parseRisContent, type ParsedRisEntry } from "~/lib/ris/parser";
import { requireProjectAccess, requireUser } from "~/lib/server/helpers.server";
import {
  computeFingerprintStrict,
  computeFingerprintStrong,
  computeSourceContentHash,
  normalizeDoiForStorage,
  normalizeTitleForStorage,
  normalizeVolumeForStorage,
} from "./fingerprint";
import type { Author } from "./identity-resolution";

// Simple structured logger
const log = {
  info: (message: string, data?: Record<string, unknown>) => {
    console.log(
      JSON.stringify({
        level: "info",
        message,
        ...data,
        timestamp: new Date().toISOString(),
      }),
    );
  },
  error: (message: string, error?: unknown, data?: Record<string, unknown>) => {
    const errorInfo =
      error instanceof Error
        ? { errorMessage: error.message, errorStack: error.stack }
        : { errorMessage: String(error) };
    console.error(
      JSON.stringify({
        level: "error",
        message,
        ...errorInfo,
        ...data,
        timestamp: new Date().toISOString(),
      }),
    );
  },
  warn: (message: string, data?: Record<string, unknown>) => {
    console.warn(
      JSON.stringify({
        level: "warn",
        message,
        ...data,
        timestamp: new Date().toISOString(),
      }),
    );
  },
};

type SourceName = (typeof sourceNameEnum.enumValues)[number];
type CslType = (typeof cslTypeEnum.enumValues)[number];

interface ImportResult {
  imported: number;
  created: number;
  failed: number;
  batchId: string;
  duplicatesRemoved: number;
}

async function getNextScreeningOrdinal(
  tx: Pick<typeof db, "select">,
  projectId: string,
): Promise<number> {
  const result = await tx
    .select({
      maxOrdinal: sql<number>`coalesce(max(${projectCitations.screeningOrdinal}), 0)`,
    })
    .from(projectCitations)
    .where(eq(projectCitations.projectId, projectId));

  return (result[0]?.maxOrdinal ?? 0) + 1;
}

/**
 * Parse author string formats into CSL-style author objects.
 */
function parseAuthorsToCSL(authorsStr: string | null): Author[] | null {
  if (!authorsStr) return null;

  try {
    const parsed = JSON.parse(authorsStr);
    if (Array.isArray(parsed)) {
      if (parsed.length > 0 && typeof parsed[0] === "object" && "family" in parsed[0]) {
        return parsed as Author[];
      }
      return parsed
        .map((name: string) => parseAuthorName(name))
        .filter(Boolean) as Author[];
    }
  } catch {
    // Not JSON, parse as string
  }

  const separator = authorsStr.includes(";") ? ";" : ",";
  const names = authorsStr
    .split(separator)
    .map((s) => s.trim())
    .filter(Boolean);
  return names.map(parseAuthorName).filter(Boolean) as Author[];
}

function parseAuthorName(name: string): Author | null {
  if (!name || !name.trim()) return null;

  const trimmed = name.trim();

  if (trimmed.includes(",")) {
    const [family, given] = trimmed.split(",", 2).map((s) => s.trim());
    return { family, given: given || undefined };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { family: parts[0] };
  }

  const family = parts.pop()!;
  const given = parts.join(" ");
  return { family, given };
}

function detectSourceName(
  database: string | null | undefined,
  filename?: string,
): SourceName {
  const dbLower = (database || "").toLowerCase();
  const fileLower = (filename || "").toLowerCase();

  if (dbLower.includes("pubmed") || fileLower.includes("pubmed")) return "pubmed";
  if (dbLower.includes("embase") || fileLower.includes("embase")) return "embase";
  if (dbLower.includes("scopus") || fileLower.includes("scopus")) return "scopus";
  if (
    dbLower.includes("web of science") ||
    dbLower.includes("wos") ||
    fileLower.includes("wos")
  )
    return "web_of_science";
  if (dbLower.includes("cochrane") || fileLower.includes("cochrane"))
    return "cochrane_library";
  if (dbLower.includes("cinahl") || fileLower.includes("cinahl")) return "cinahl";
  if (dbLower.includes("psycinfo") || fileLower.includes("psycinfo")) return "psycinfo";

  return "other";
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (!value) continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function mapRisTypeToCslType(entry: ParsedRisEntry): CslType {
  const risType = (entry.type ?? "").trim().toUpperCase();
  const subtype = (entry.subtype ?? "").trim().toLowerCase();
  const journal = firstNonEmpty(entry.journal, entry.journalAbbrev)?.toLowerCase() ?? "";

  if (subtype.includes("conference")) return "paper-conference";
  if (subtype.includes("review")) return "review";
  if (subtype.includes("dataset")) return "dataset";
  if (subtype.includes("thesis")) return "thesis";

  if (journal.includes("proceedings") || journal.includes("lecture notes")) {
    return "paper-conference";
  }

  switch (risType) {
    case "JOUR":
      return "article-journal";
    case "CHAP":
    case "ECHAP":
      return "chapter";
    case "BOOK":
    case "EBOOK":
      return "book";
    case "CONF":
    case "CPAPER":
      return "paper-conference";
    case "RPRT":
      return "report";
    case "THES":
      return "thesis";
    case "DATA":
      return "dataset";
    case "ELEC":
    case "WEB":
      return "webpage";
    default:
      return "article";
  }
}

function mapRisTypeToSourcePublicationType(risType: string): string | null {
  const normalized = risType.trim().toUpperCase();
  switch (normalized) {
    case "JOUR":
      return "Journal Article";
    case "CONF":
    case "CPAPER":
      return "Conference Paper";
    case "CHAP":
    case "ECHAP":
      return "Book Chapter";
    case "BOOK":
    case "EBOOK":
      return "Book";
    case "RPRT":
      return "Report";
    case "THES":
      return "Thesis";
    case "DATA":
      return "Dataset";
    case "ELEC":
    case "WEB":
      return "Web Page";
    default:
      return null;
  }
}

function dedupeCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(trimmed);
  }

  return deduped;
}

function extractSourcePublicationTypes(rawPayload: unknown): string[] {
  if (!rawPayload || typeof rawPayload !== "object" || Array.isArray(rawPayload)) {
    return [];
  }

  const payload = rawPayload as Record<string, unknown>;
  const values: string[] = [];

  if (Array.isArray(payload.publicationTypes)) {
    for (const publicationType of payload.publicationTypes) {
      if (typeof publicationType === "string") {
        values.push(publicationType);
      }
    }
  }

  if (typeof payload.subtype === "string") {
    values.push(payload.subtype);
  }

  if (typeof payload.type === "string") {
    const mapped = mapRisTypeToSourcePublicationType(payload.type);
    values.push(mapped ?? payload.type);
  }

  return dedupeCaseInsensitive(values);
}

/**
 * Mark a batch as failed with error message
 */
async function markBatchFailed(
  batchId: string,
  error: unknown,
  counts?: { created: number; existing: number; failed: number },
) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  try {
    await db
      .update(importBatches)
      .set({
        status: "failed",
        errorMessage,
        recordsImported: counts ? counts.created + counts.existing : null,
        recordsDuplicated: counts?.existing ?? null,
        recordsFailed: counts?.failed ?? null,
        completedAt: new Date(),
      })
      .where(eq(importBatches.id, batchId));
  } catch (updateError) {
    log.error("Failed to update batch status", updateError, { batchId });
  }
}

// Validation schemas
const importRisSchema = z.object({
  projectId: z.string(),
  risContent: z.string(),
  fileName: z.string().optional(),
  sourceName: z
    .enum([
      "pubmed",
      "embase",
      "scopus",
      "web_of_science",
      "cochrane_library",
      "cinahl",
      "psycinfo",
      "other",
    ])
    .optional(),
});

const importPubmedSchema = z.object({
  projectId: z.string(),
  pmids: z.string(),
});

const getProjectCitationsSchema = z.object({
  projectId: z.string(),
});

const getCitationDetailSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
});

/**
 * Prepare a single RIS entry for batch import.
 * Computes all fingerprints and normalized fields.
 */
async function prepareRisEntry(
  entry: ParsedRisEntry,
  projectId: string,
  batchId: string,
  batchSourceName: SourceName,
  fileName?: string,
) {
  const authors = parseAuthorsToCSL(
    entry.authors.length > 0 ? JSON.stringify(entry.authors) : null,
  );
  const authorsJson = authors ? JSON.stringify(authors) : null;

  const fileFingerprint = await computeSourceContentHash(
    entry.pmid,
    entry.doi,
    entry.title,
    entry.year,
    authorsJson,
  );

  if (!fileFingerprint) {
    throw new Error(`Could not compute fingerprint for entry: ${entry.title}`);
  }

  const title = entry.title || "Untitled";
  const containerTitle = firstNonEmpty(entry.journal, entry.journalAbbrev);
  const titleNorm = normalizeTitleForStorage(title);
  const doiNorm = normalizeDoiForStorage(entry.doi);
  const volumeNorm = normalizeVolumeForStorage(entry.volume);
  const cslType = mapRisTypeToCslType(entry);
  const referenceFingerprint = await computeFingerprintStrong(
    title,
    entry.year,
    authorsJson,
  );
  const referenceFingerprintStrict = await computeFingerprintStrict(
    title,
    entry.year,
    authorsJson,
    entry.volume,
  );

  const entrySourceName = entry.database
    ? detectSourceName(entry.database, fileName)
    : batchSourceName;

  // Generate all IDs upfront
  const canonicalReferenceId = nanoid();
  const projectCitationId = nanoid();
  const sourceRecordId = nanoid();
  const citationSourceLinkId = nanoid();

  return {
    canonicalReference: {
      id: canonicalReferenceId,
      projectId,
      type: cslType,
      title,
      titleNorm,
      abstract: entry.abstract ?? null,
      authors,
      year: entry.year ?? null,
      containerTitle,
      doi: entry.doi ?? null,
      doiNorm,
      pmid: entry.pmid ?? null,
      pmcid: null as string | null,
      volume: entry.volume ?? null,
      volumeNorm,
      issue: entry.issue ?? null,
      pages: entry.pages ?? null,
      publisher: entry.publisher ?? null,
      referenceFingerprint,
      referenceFingerprintStrict,
    },
    projectCitation: {
      id: projectCitationId,
      projectId,
      canonicalReferenceId,
      screeningOrdinal: 0,
    },
    sourceRecord: {
      id: sourceRecordId,
      projectId,
      importBatchId: batchId,
      sourceName: entrySourceName,
      externalId: firstNonEmpty(entry.pmid, entry.sourceExternalId),
      rawPayload: entry,
      fileFingerprint,
    },
    citationSourceLink: {
      id: citationSourceLinkId,
      canonicalReferenceId,
      sourceRecordId,
      isPrimarySource: true,
      linkConfidence: "fingerprint" as const,
    },
  };
}

/**
 * Prepare a single NBIB/PubMed entry for batch import.
 * Computes all fingerprints and normalized fields.
 */
async function prepareNbibEntry(
  entry: ParsedNbibEntry,
  projectId: string,
  batchId: string,
) {
  const authors = parseAuthorsToCSL(
    entry.authors.length > 0 ? JSON.stringify(entry.authors) : null,
  );
  const authorsJson = authors ? JSON.stringify(authors) : null;

  // For PubMed entries, use PMID as the fingerprint
  const fileFingerprint = `pmid:${entry.pmid}`;

  const title =
    firstNonEmpty(entry.title, entry.bookTitle, entry.collectionTitle) ?? "Untitled";
  const containerTitle = firstNonEmpty(
    entry.journal,
    entry.journalAbbrev,
    entry.collectionTitle,
  );
  const titleNorm = normalizeTitleForStorage(title);
  const doiNorm = normalizeDoiForStorage(entry.doi);
  const volumeNorm = normalizeVolumeForStorage(entry.volume);
  const referenceFingerprint = await computeFingerprintStrong(
    title,
    entry.year,
    authorsJson,
  );
  const referenceFingerprintStrict = await computeFingerprintStrict(
    title,
    entry.year,
    authorsJson,
    entry.volume,
  );

  // Generate all IDs upfront
  const canonicalReferenceId = nanoid();
  const projectCitationId = nanoid();
  const sourceRecordId = nanoid();
  const citationSourceLinkId = nanoid();

  return {
    canonicalReference: {
      id: canonicalReferenceId,
      projectId,
      title,
      titleNorm,
      abstract: entry.abstract ?? null,
      authors,
      year: entry.year ?? null,
      containerTitle,
      doi: entry.doi ?? null,
      doiNorm,
      pmid: entry.pmid,
      pmcid: entry.pmcid ?? null,
      volume: entry.volume ?? null,
      volumeNorm,
      issue: entry.issue ?? null,
      pages: entry.pages ?? null,
      publisher: firstNonEmpty(entry.publisher),
      referenceFingerprint,
      referenceFingerprintStrict,
    },
    projectCitation: {
      id: projectCitationId,
      projectId,
      canonicalReferenceId,
      screeningOrdinal: 0,
    },
    sourceRecord: {
      id: sourceRecordId,
      projectId,
      importBatchId: batchId,
      sourceName: "pubmed" as const,
      externalId: entry.pmid,
      rawPayload: entry,
      fileFingerprint,
    },
    citationSourceLink: {
      id: citationSourceLinkId,
      canonicalReferenceId,
      sourceRecordId,
      isPrimarySource: true,
      linkConfidence: "fingerprint" as const,
    },
  };
}

export const $importRisCitations = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => importRisSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    log.info("Starting citation import", {
      projectId: data.projectId,
      userId: user.id,
      fileName: data.fileName,
      contentLength: data.risContent.length,
    });

    // Detect format: PubMed NBIB or RIS
    const formatIsNbib = isNbibFormat(data.risContent);

    // Parse entries based on detected format
    let nbibEntries: ParsedNbibEntry[] = [];
    let risEntries: ParsedRisEntry[] = [];

    if (formatIsNbib) {
      nbibEntries = parseNbibContent(data.risContent);
      log.info("Parsed PubMed NBIB entries", {
        projectId: data.projectId,
        entryCount: nbibEntries.length,
      });

      if (nbibEntries.length === 0) {
        throw new Error("No valid citations found in the PubMed file");
      }
    } else {
      // Validate RIS format
      const hasTY = /^TY\s+-/m.test(data.risContent);
      const hasER = /^ER\s+-/m.test(data.risContent);
      if (!hasTY || !hasER) {
        log.warn("Invalid citation format", { projectId: data.projectId, hasTY, hasER });
        throw new Error(
          "Invalid format: file must be RIS (with TY and ER tags) or PubMed format (with PMID tags)",
        );
      }

      risEntries = parseRisContent(data.risContent);
      log.info("Parsed RIS entries", {
        projectId: data.projectId,
        entryCount: risEntries.length,
      });

      if (risEntries.length === 0) {
        throw new Error("No valid citations found in the RIS file");
      }
    }

    const totalEntries = formatIsNbib ? nbibEntries.length : risEntries.length;
    const sourceType = formatIsNbib ? "nbib" : "ris";
    const batchSourceName: SourceName = formatIsNbib
      ? "pubmed"
      : (data.sourceName ?? detectSourceName(null, data.fileName));

    // Create import batch
    const batchId = nanoid();
    await db.insert(importBatches).values({
      id: batchId,
      projectId: data.projectId,
      sourceType,
      sourceName: batchSourceName,
      filename: data.fileName || null,
      recordsTotal: totalEntries,
      importedBy: user.id,
      status: "processing",
      startedAt: new Date(),
    });

    log.info("Created import batch", {
      batchId,
      projectId: data.projectId,
      recordsTotal: totalEntries,
      sourceType,
    });

    const importStartTime = Date.now();

    try {
      // Phase 1: Prepare all entries in parallel (compute fingerprints, normalize fields)
      const prepareStartTime = Date.now();

      const preparedResults = formatIsNbib
        ? await Promise.allSettled(
            nbibEntries.map((entry) => prepareNbibEntry(entry, data.projectId, batchId)),
          )
        : await Promise.allSettled(
            risEntries.map((entry) =>
              prepareRisEntry(
                entry,
                data.projectId,
                batchId,
                batchSourceName,
                data.fileName,
              ),
            ),
          );

      const prepareMs = Date.now() - prepareStartTime;
      log.info("Prepared entries", { batchId, prepareMs });

      // Separate successful and failed preparations
      type PreparedEntry =
        | Awaited<ReturnType<typeof prepareRisEntry>>
        | Awaited<ReturnType<typeof prepareNbibEntry>>;
      const prepared: PreparedEntry[] = [];
      let failed = 0;

      for (const result of preparedResults) {
        if (result.status === "fulfilled") {
          prepared.push(result.value);
        } else {
          failed++;
          log.error("Failed to prepare entry", result.reason, { batchId });
        }
      }

      if (prepared.length === 0) {
        throw new Error("All entries failed to prepare");
      }

      // Phase 2: Batch insert all records in a transaction
      const insertStartTime = Date.now();
      await db.transaction(async (tx) => {
        // Insert all canonical references
        await tx
          .insert(canonicalReferences)
          .values(prepared.map((p) => p.canonicalReference));

        // Insert all project citations with immutable per-project ordinals
        const nextScreeningOrdinal = await getNextScreeningOrdinal(tx, data.projectId);
        await tx.insert(projectCitations).values(
          prepared.map((p, index) => ({
            ...p.projectCitation,
            screeningOrdinal: nextScreeningOrdinal + index,
          })),
        );

        // Insert all source records
        await tx.insert(sourceRecords).values(prepared.map((p) => p.sourceRecord));

        // Insert all citation source links
        await tx
          .insert(citationSourceLinks)
          .values(prepared.map((p) => p.citationSourceLink));
      });
      const insertMs = Date.now() - insertStartTime;
      log.info("Batch insert complete", {
        batchId,
        insertMs,
        recordCount: prepared.length,
      });

      const created = prepared.length;
      const totalImportMs = Date.now() - importStartTime;
      log.info("Entry processing complete", {
        batchId,
        created,
        failed,
        totalImportMs,
        avgMs: Math.round(totalImportMs / totalEntries),
      });

      // Fetch deduplication mode from settings
      const dedupSettings = await db.query.projectScreeningSettings.findFirst({
        where: eq(projectScreeningSettings.projectId, data.projectId),
      });
      const dedupMode = (
        dedupSettings?.deduplicationMode === "manual" ? "manual" : "automatic"
      ) as "automatic" | "manual";

      // Run duplicate detection after import
      log.info("Running duplicate detection", {
        batchId,
        projectId: data.projectId,
        dedupMode,
      });
      const detectionResult = await detectDuplicatesForProject(data.projectId, dedupMode);
      log.info("Duplicate detection complete", {
        batchId,
        groupsCreated: detectionResult.groupsCreated,
        duplicatesRemoved: detectionResult.duplicatesRemoved,
      });

      // Update batch with final counts
      await db
        .update(importBatches)
        .set({
          recordsImported: created,
          recordsDuplicated: detectionResult.duplicatesRemoved,
          recordsFailed: failed,
          status: "completed",
          completedAt: new Date(),
        })
        .where(eq(importBatches.id, batchId));

      log.info("Citation import completed", {
        batchId,
        created,
        failed,
        duplicatesRemoved: detectionResult.duplicatesRemoved,
      });

      return {
        imported: totalEntries,
        created,
        failed,
        batchId,
        duplicatesRemoved: detectionResult.duplicatesRemoved,
      } satisfies ImportResult;
    } catch (error) {
      log.error("Citation import failed", error, { batchId });
      await markBatchFailed(batchId, error);
      throw error;
    }
  });

/**
 * Prepare a single PubMed entry for batch import.
 * Computes all fingerprints and normalized fields.
 */
async function preparePubmedEntry(
  entry: ParsedPubmedEntry,
  projectId: string,
  batchId: string,
) {
  const authors = parseAuthorsToCSL(
    entry.authors.length > 0 ? JSON.stringify(entry.authors) : null,
  );
  const authorsJson = authors ? JSON.stringify(authors) : null;

  const title = entry.title || "Untitled";
  const titleNorm = normalizeTitleForStorage(title);
  const doiNorm = normalizeDoiForStorage(entry.doi);
  const volumeNorm = normalizeVolumeForStorage(entry.volume);
  const referenceFingerprint = await computeFingerprintStrong(
    title,
    entry.year,
    authorsJson,
  );
  const referenceFingerprintStrict = await computeFingerprintStrict(
    title,
    entry.year,
    authorsJson,
    entry.volume,
  );

  // For PubMed, use PMID as the fingerprint
  const fileFingerprint = `pmid:${entry.pmid}`;

  // Generate all IDs upfront
  const canonicalReferenceId = nanoid();
  const projectCitationId = nanoid();
  const sourceRecordId = nanoid();
  const citationSourceLinkId = nanoid();

  return {
    canonicalReference: {
      id: canonicalReferenceId,
      projectId,
      title,
      titleNorm,
      abstract: entry.abstract ?? null,
      authors,
      year: entry.year ?? null,
      containerTitle: entry.journal ?? null,
      doi: entry.doi ?? null,
      doiNorm,
      pmid: entry.pmid,
      pmcid: null,
      volume: entry.volume ?? null,
      volumeNorm,
      issue: entry.issue ?? null,
      pages: null,
      publisher: null,
      referenceFingerprint,
      referenceFingerprintStrict,
    },
    projectCitation: {
      id: projectCitationId,
      projectId,
      canonicalReferenceId,
      screeningOrdinal: 0,
    },
    sourceRecord: {
      id: sourceRecordId,
      projectId,
      importBatchId: batchId,
      sourceName: "pubmed" as const,
      externalId: entry.pmid,
      rawPayload: entry,
      fileFingerprint,
    },
    citationSourceLink: {
      id: citationSourceLinkId,
      canonicalReferenceId,
      sourceRecordId,
      isPrimarySource: true,
      linkConfidence: "fingerprint" as const,
    },
  };
}

export const $importPubmedCitations = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => importPubmedSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    log.info("Starting PubMed import", {
      projectId: data.projectId,
      userId: user.id,
      pmidsInput: data.pmids.substring(0, 100),
    });

    // Parse and validate PMIDs
    const pmidList = data.pmids
      .split(/[\s,]+/)
      .map((p: string) => p.trim())
      .filter(Boolean);

    if (pmidList.length === 0) {
      log.warn("No PMIDs provided", { projectId: data.projectId });
      throw new Error("At least one PMID required");
    }

    if (!pmidList.every((p: string) => /^\d+$/.test(p))) {
      log.warn("Invalid PMID format", {
        projectId: data.projectId,
        pmids: pmidList.slice(0, 5),
      });
      throw new Error("Invalid PMID format - PMIDs must be numeric");
    }

    log.info("Validated PMIDs", {
      projectId: data.projectId,
      pmidCount: pmidList.length,
    });

    // Fetch from PubMed API
    let xml: string;
    try {
      xml = await fetchPubmedCitations(pmidList);
    } catch (fetchError) {
      log.error("Failed to fetch from PubMed API", fetchError, {
        projectId: data.projectId,
        pmidCount: pmidList.length,
      });
      throw new Error(
        `Failed to fetch from PubMed: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`,
      );
    }

    // Parse the XML response
    let entries: ParsedPubmedEntry[];
    try {
      entries = parsePubmedXml(xml);
    } catch (parseError) {
      log.error("Failed to parse PubMed XML", parseError, {
        projectId: data.projectId,
        xmlLength: xml.length,
        xmlPreview: xml.substring(0, 200),
      });
      throw new Error(
        `Failed to parse PubMed response: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
      );
    }

    log.info("Parsed PubMed entries", {
      projectId: data.projectId,
      entryCount: entries.length,
    });

    if (entries.length === 0) {
      log.warn("No valid citations found", {
        projectId: data.projectId,
        pmidCount: pmidList.length,
      });
      throw new Error("No valid citations found for the provided PMIDs");
    }

    // Create import batch
    const batchId = nanoid();
    await db.insert(importBatches).values({
      id: batchId,
      projectId: data.projectId,
      sourceType: "pubmed_api",
      sourceName: "pubmed",
      filename: null,
      recordsTotal: entries.length,
      importedBy: user.id,
      status: "processing",
      startedAt: new Date(),
    });

    log.info("Created import batch", {
      batchId,
      projectId: data.projectId,
      recordsTotal: entries.length,
    });

    const importStartTime = Date.now();

    try {
      // Phase 1: Prepare all entries in parallel (compute fingerprints, normalize fields)
      const prepareStartTime = Date.now();
      const preparedResults = await Promise.allSettled(
        entries.map((entry) => preparePubmedEntry(entry, data.projectId, batchId)),
      );
      const prepareMs = Date.now() - prepareStartTime;
      log.info("Prepared entries", { batchId, prepareMs });

      // Separate successful and failed preparations
      const prepared: Awaited<ReturnType<typeof preparePubmedEntry>>[] = [];
      let failed = 0;

      for (const result of preparedResults) {
        if (result.status === "fulfilled") {
          prepared.push(result.value);
        } else {
          failed++;
          log.error("Failed to prepare entry", result.reason, { batchId });
        }
      }

      if (prepared.length === 0) {
        throw new Error("All entries failed to prepare");
      }

      // Phase 2: Batch insert all records in a transaction
      const insertStartTime = Date.now();
      await db.transaction(async (tx) => {
        // Insert all canonical references
        await tx
          .insert(canonicalReferences)
          .values(prepared.map((p) => p.canonicalReference));

        // Insert all project citations with immutable per-project ordinals
        const nextScreeningOrdinal = await getNextScreeningOrdinal(tx, data.projectId);
        await tx.insert(projectCitations).values(
          prepared.map((p, index) => ({
            ...p.projectCitation,
            screeningOrdinal: nextScreeningOrdinal + index,
          })),
        );

        // Insert all source records
        await tx.insert(sourceRecords).values(prepared.map((p) => p.sourceRecord));

        // Insert all citation source links
        await tx
          .insert(citationSourceLinks)
          .values(prepared.map((p) => p.citationSourceLink));
      });
      const insertMs = Date.now() - insertStartTime;
      log.info("Batch insert complete", {
        batchId,
        insertMs,
        recordCount: prepared.length,
      });

      const created = prepared.length;
      const totalImportMs = Date.now() - importStartTime;
      log.info("Entry processing complete", {
        batchId,
        created,
        failed,
        totalImportMs,
        avgMs: Math.round(totalImportMs / entries.length),
      });

      // Fetch deduplication mode from settings
      const dedupSettings = await db.query.projectScreeningSettings.findFirst({
        where: eq(projectScreeningSettings.projectId, data.projectId),
      });
      const dedupMode = (
        dedupSettings?.deduplicationMode === "manual" ? "manual" : "automatic"
      ) as "automatic" | "manual";

      // Run duplicate detection after import
      log.info("Running duplicate detection", {
        batchId,
        projectId: data.projectId,
        dedupMode,
      });
      const detectionResult = await detectDuplicatesForProject(data.projectId, dedupMode);
      log.info("Duplicate detection complete", {
        batchId,
        groupsCreated: detectionResult.groupsCreated,
        duplicatesRemoved: detectionResult.duplicatesRemoved,
      });

      // Update batch with final counts
      await db
        .update(importBatches)
        .set({
          recordsImported: created,
          recordsDuplicated: detectionResult.duplicatesRemoved,
          recordsFailed: failed,
          status: "completed",
          completedAt: new Date(),
        })
        .where(eq(importBatches.id, batchId));

      log.info("PubMed import completed", {
        batchId,
        created,
        failed,
        duplicatesRemoved: detectionResult.duplicatesRemoved,
      });

      return {
        imported: entries.length,
        created,
        failed,
        batchId,
        duplicatesRemoved: detectionResult.duplicatesRemoved,
      } satisfies ImportResult;
    } catch (error) {
      log.error("PubMed import failed", error, { batchId });
      await markBatchFailed(batchId, error);
      throw error;
    }
  });

export const $getProjectCitations = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => getProjectCitationsSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    log.info("Fetching project citations", {
      projectId: data.projectId,
      userId: user.id,
    });

    const citations = await db.query.projectCitations.findMany({
      where: eq(projectCitations.projectId, data.projectId),
      with: {
        canonicalReference: {
          with: {
            sourceLinks: {
              columns: {
                isPrimarySource: true,
              },
              with: {
                sourceRecord: {
                  columns: {
                    sourceName: true,
                    rawPayload: true,
                  },
                },
              },
            },
          },
        },
      },
      orderBy: (pc, { asc }) => [asc(pc.createdAt)],
    });

    log.info("Fetched citations", { projectId: data.projectId, count: citations.length });

    return citations.map((pc) => {
      const sourceNames = Array.from(
        new Set(
          pc.canonicalReference.sourceLinks
            .map((link) => link.sourceRecord?.sourceName)
            .filter((name): name is SourceName => Boolean(name)),
        ),
      );

      const primarySourceName =
        pc.canonicalReference.sourceLinks.find(
          (link) => link.isPrimarySource && link.sourceRecord?.sourceName,
        )?.sourceRecord?.sourceName ??
        sourceNames[0] ??
        null;

      const sourcePublicationTypes = dedupeCaseInsensitive(
        pc.canonicalReference.sourceLinks.flatMap((link) =>
          extractSourcePublicationTypes(link.sourceRecord?.rawPayload),
        ),
      );

      return {
        id: pc.id,
        projectId: pc.projectId,
        canonicalReferenceId: pc.canonicalReferenceId,
        citationType: pc.canonicalReference.type,
        screeningStatus: pc.screeningStatus,
        exclusionReason: pc.exclusionReason,
        exclusionCode: pc.exclusionCode,
        duplicateStatus: pc.duplicateStatus,
        duplicateGroupId: pc.duplicateGroupId,
        consensusStatus: pc.consensusStatus,
        reviewCount: pc.reviewCount,
        notes: pc.notes,
        createdAt: pc.createdAt,
        updatedAt: pc.updatedAt,
        title: pc.canonicalReference.title,
        authors: pc.canonicalReference.authors as Author[] | null,
        abstract: pc.canonicalReference.abstract,
        year: pc.canonicalReference.year,
        containerTitle: pc.canonicalReference.containerTitle,
        doi: pc.canonicalReference.doi,
        pmid: pc.canonicalReference.pmid,
        volume: pc.canonicalReference.volume,
        issue: pc.canonicalReference.issue,
        pages: pc.canonicalReference.pages,
        primarySourceName,
        sourceNames,
        sourcePublicationTypes,
      };
    });
  });

export const $getCitationDetail = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => getCitationDetailSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const citation = await db.query.projectCitations.findFirst({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.id, data.citationId),
      ),
      with: {
        canonicalReference: true,
      },
    });

    if (!citation) {
      throw new Error("Citation not found");
    }

    const decisions = await db
      .select({
        id: screeningDecisions.id,
        decision: screeningDecisions.decision,
        decisionOrder: screeningDecisions.decisionOrder,
        exclusionCode: screeningDecisions.exclusionCode,
        exclusionNotes: screeningDecisions.exclusionNotes,
        notes: screeningDecisions.notes,
        reviewerId: screeningDecisions.reviewerId,
        reviewerName: dbUser.name,
        reviewerImage: dbUser.image,
        createdAt: screeningDecisions.createdAt,
      })
      .from(screeningDecisions)
      .innerJoin(dbUser, eq(screeningDecisions.reviewerId, dbUser.id))
      .where(
        and(
          eq(screeningDecisions.projectCitationId, data.citationId),
          isNull(screeningDecisions.deletedAt),
        ),
      )
      .orderBy(screeningDecisions.createdAt);

    const timeline = [
      {
        type: "imported" as const,
        label: "Imported",
        createdAt: citation.createdAt,
        details: null,
      },
      ...(citation.screenedAt
        ? [
            {
              type: "screened" as const,
              label: "Screened",
              createdAt: citation.screenedAt,
              details: citation.screeningStatus ?? "pending",
            },
          ]
        : []),
      ...(citation.finalDecisionAt
        ? [
            {
              type: "final_decision" as const,
              label: "Final decision",
              createdAt: citation.finalDecisionAt,
              details: citation.finalDecision ?? null,
            },
          ]
        : []),
    ].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    return {
      id: citation.id,
      projectId: citation.projectId,
      canonicalReferenceId: citation.canonicalReferenceId,
      screeningStatus: citation.screeningStatus,
      exclusionReason: citation.exclusionReason,
      exclusionCode: citation.exclusionCode,
      duplicateStatus: citation.duplicateStatus,
      duplicateGroupId: citation.duplicateGroupId,
      consensusStatus: citation.consensusStatus,
      finalDecision: citation.finalDecision,
      reviewCount: citation.reviewCount,
      notes: citation.notes,
      createdAt: citation.createdAt,
      updatedAt: citation.updatedAt,
      title: citation.canonicalReference.title,
      authors: citation.canonicalReference.authors as Author[] | null,
      abstract: citation.canonicalReference.abstract,
      year: citation.canonicalReference.year,
      containerTitle: citation.canonicalReference.containerTitle,
      doi: citation.canonicalReference.doi,
      pmid: citation.canonicalReference.pmid,
      volume: citation.canonicalReference.volume,
      issue: citation.canonicalReference.issue,
      pages: citation.canonicalReference.pages,
      history: {
        decisions: decisions.map((d) => ({
          ...d,
          reviewerName: d.reviewerName ?? "Unknown reviewer",
        })),
        timeline,
      },
    };
  });
